<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2022-04-28 11:57:37
 * @LastEditors: luckymiaow
-->
<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import { BorderOutlined, SwitcherOutlined } from '@ant-design/icons-vue';
import { Modal as AModal } from 'ant-design-vue';
import useConfigProvider from '../../config-provider/src/useConfigProvider';
import type { Props } from '../types';
import useSlotsHooks from '../../hooks/useSlot';
import useMove from './useMove';

export default defineComponent({
  name: 'Modal',
  components: {
    AModal,
    SwitcherOutlined,
    BorderOutlined,
  },
  props: {
    fullScreen: { type: Boolean, default: undefined }, // default: true
    fullModal: { type: Boolean, default: undefined }, // default: false
  },
  setup(props, { slots, attrs }) {
    const isFullScreen = ref(false);
    const injectConfig = useConfigProvider().injectConfig<Props>('modal', props);
    const getBindValue = computed(() => ({
      ...injectConfig?.value,
      ...attrs,
      title: undefined,
      style: undefined,
    }));

    const title = computed(() => {
      return attrs?.title;
    });

    const windowSize = {
      top: '0px',
      right: '47px',
    };

    watch(
      () => getBindValue.value.closable,
      (val: boolean) => {
        if (val === false) {
          windowSize.right = '0px';
        } else {
          windowSize.right = '47px';
        }
      },
      { immediate: true }
    );

    watch(
      () => getBindValue.value.fullModal,
      (val: boolean) => {
        isFullScreen.value = val;
      },
      { immediate: true }
    );

    const moveRef = ref<HTMLDivElement>();

    const { style, addMove } = useMove();

    const { useExcludeSlots } = useSlotsHooks({ slots }, ['default', 'title']);

    return {
      isFullScreen,
      windowSize,
      useExcludeSlots,
      injectConfig,
      getBindValue,
      title,
      style,
      addMove,
      moveRef,
    };
  },
});
</script>

<template>
  <div>
    <AModal v-bind="getBindValue" :style="style" class="c-modal" :class="{ 'config-fullScreen': isFullScreen }">
      <span v-if="getBindValue.fullScreen !== false" class="tools-window-size" :style="windowSize">
        <SwitcherOutlined v-if="isFullScreen" @click="isFullScreen = false" />
        <BorderOutlined v-else @click="isFullScreen = true"
      /></span>
      <slot></slot>
      <template #title>
        <slot name="title">
          <div ref="moveRef" @vue:mounted="addMove(moveRef)">{{ $attrs.title }}</div>
        </slot>
      </template>
      <template v-for="(slot, key) in useExcludeSlots" :key="key" #[key]="scope">
        <slot :name="key" v-bind="scope ?? {}"> </slot>
      </template>
    </AModal>
  </div>
</template>

<style scoped lang="less">
.tools-window-size {
  position: absolute;
  z-index: 2;
  height: 56px;
  width: 50px;
  line-height: 56px;
  text-align: center;
  .anticon {
    cursor: pointer;
  }
}
</style>

<style lang="less">
.c-modal.config-fullScreen.ant-modal {
  top: 0 !important;
  left: 0 !important;
  padding: 0;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0;
  .ant-modal-content {
    min-height: 100vh !important;
    border-radius: 0;
  }
  .ant-modal-header {
    cursor: auto;
  }
}
.c-modal {
  .ant-modal-header {
    cursor: move;
  }
}
</style>
